/**
 * Pure matching logic: sidecar (IP, x, y) entries → CCTV graph elements.
 *
 * JointJS-agnostic: accepts any object matching the minimal `CctvElementLike`
 * shape so unit tests can inject plain mocks (same approach as excel-io exporter).
 *
 * ## Coordinate problem
 * The sidecar `x,y` are in DXF METER space. But placed CCTV elements in a saved
 * map do NOT carry meter coordinates — they store on-canvas PAPER coordinates
 * (`data.vertices` / `data.originalPosition`), scaled and translated by the
 * import + normalizeMapScale. The two clouds are the SAME cameras related by a
 * uniform similarity (scale + translation, with a possible Y flip — screen Y
 * grows downward). So we ESTIMATE that transform from the two point clouds
 * (mapping-independent, reload-safe) and then match nearest pairs.
 *
 * If the clouds cannot be aligned (wrong file, rotated map, too few cameras),
 * `aligned` is false and no updates are produced — we fail visibly rather than
 * assign wrong IPs.
 *
 * ## Handle path (preferred)
 * Sidecars built from the same conversion as the map carry the CSV
 * `EntityHandle` per item, and placed elements keep it as `data.entityHandle`.
 * When enough items resolve by handle we match 1:1 and skip alignment entirely —
 * immune to duplicate camera points, partial IP labelling and rotation.
 */

import type {
  CctvIpMatchResult,
  CctvIpSidecar,
  CctvIpUpdate,
  SidecarItem,
} from '../model/types'

/** Minimal element shape the matcher needs from the graph. */
export interface CctvElementLike {
  id: string | number
  get(key: 'data'):
    | {
        layer?: string
        typeId?: string
        objectType?: string
        type?: string
        /** Paper-space single vertex for point entities (CCTV are points). */
        vertices?: Array<[number, number]>
        /** Paper-space snapshot used as a fallback when vertices is absent. */
        originalPosition?: { x: number; y: number }
        /** Legacy meter-space field (fresh imports only); used if present. */
        originalCoords?: { x: number; y: number }
        /** CSV EntityHandle carried from import; pairs with `SidecarItem.handle`. */
        entityHandle?: string
        properties?: Record<string, unknown>
      }
    | undefined
}

interface Pt {
  x: number
  y: number
}

const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/

/** Minimum cameras/items needed to estimate a transform reliably. */
const MIN_POINTS_FOR_TRANSFORM = 3
/** Accept the alignment only if at least this fraction of items find a camera. */
const MIN_INLIER_FRACTION = 0.5
/** A matched pair is an inlier if its residual is below this × camera spacing. */
const INLIER_SPACING_FRACTION = 0.3
/**
 * Use the handle path only when at least this fraction of handle-bearing items
 * find a camera — otherwise the sidecar is from another conversion (handles are
 * per-conversion counters) and we fall back to coordinate alignment.
 */
const MIN_HANDLE_HIT_FRACTION = 0.5

/**
 * Parse + validate sidecar JSON text. Throws a user-facing `Error` on malformed
 * input. Drops individual items that are not well-formed.
 */
export function parseSidecar(text: string): CctvIpSidecar {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    throw new Error('JSON 파싱 실패: 올바른 사이드카 파일이 아닙니다.')
  }
  if (!raw || typeof raw !== 'object') {
    throw new Error('사이드카 형식이 올바르지 않습니다.')
  }
  const items = (raw as { items?: unknown }).items
  if (!Array.isArray(items)) {
    throw new Error('사이드카에 items 배열이 없습니다.')
  }
  const cleaned: SidecarItem[] = []
  for (const it of items) {
    if (
      it &&
      typeof (it as SidecarItem).ip === 'string' &&
      typeof (it as SidecarItem).x === 'number' &&
      typeof (it as SidecarItem).y === 'number' &&
      IPV4_RE.test((it as SidecarItem).ip)
    ) {
      const handle = (it as SidecarItem).handle
      cleaned.push({
        ip: (it as SidecarItem).ip,
        x: (it as SidecarItem).x,
        y: (it as SidecarItem).y,
        ...(typeof handle === 'string' && handle ? { handle } : {}),
      })
    }
  }
  if (cleaned.length === 0) {
    throw new Error('사이드카에 유효한 IP 항목이 없습니다.')
  }
  return { ...(raw as object), items: cleaned } as CctvIpSidecar
}

/** True when an element's data discriminators identify it as a CCTV. */
export function isCctvElement(data: ReturnType<CctvElementLike['get']>): boolean {
  if (!data) return false
  return [data.layer, data.typeId, data.objectType, data.type]
    .filter((v): v is string => typeof v === 'string')
    .some(v => v.toLowerCase().includes('cctv'))
}

/** The element's representative point in whatever space it stores. */
export function elementPoint(data: ReturnType<CctvElementLike['get']>): Pt | null {
  if (!data) return null
  // Prefer meter-space originalCoords when present (fresh, pre-save imports);
  // otherwise use paper-space vertices/originalPosition (saved maps).
  if (data.originalCoords) return { x: data.originalCoords.x, y: data.originalCoords.y }
  if (Array.isArray(data.vertices) && data.vertices.length > 0) {
    if (data.vertices.length === 1) {
      return { x: data.vertices[0][0], y: data.vertices[0][1] }
    }
    let sx = 0
    let sy = 0
    for (const v of data.vertices) {
      sx += v[0]
      sy += v[1]
    }
    return { x: sx / data.vertices.length, y: sy / data.vertices.length }
  }
  if (data.originalPosition) return { x: data.originalPosition.x, y: data.originalPosition.y }
  return null
}

function centroid(pts: Pt[]): Pt {
  let sx = 0
  let sy = 0
  for (const p of pts) {
    sx += p.x
    sy += p.y
  }
  return { x: sx / pts.length, y: sy / pts.length }
}

function rms(pts: Pt[], c: Pt): number {
  let s = 0
  for (const p of pts) {
    const dx = p.x - c.x
    const dy = p.y - c.y
    s += dx * dx + dy * dy
  }
  return Math.sqrt(s / pts.length)
}

/** Greedy unique nearest assignment. Returns [itemIdx, camIdx, dist] sorted by distance. */
function nearestPairs(P: Pt[], Q: Pt[]): Array<[number, number, number]> {
  const pairs: Array<[number, number, number]> = []
  for (let i = 0; i < P.length; i++) {
    for (let j = 0; j < Q.length; j++) {
      const dx = P[i].x - Q[j].x
      const dy = P[i].y - Q[j].y
      pairs.push([dx * dx + dy * dy, i, j])
    }
  }
  pairs.sort((a, b) => a[0] - b[0])
  const usedI = new Set<number>()
  const usedJ = new Set<number>()
  const res: Array<[number, number, number]> = []
  for (const [d2, i, j] of pairs) {
    if (usedI.has(i) || usedJ.has(j)) continue
    usedI.add(i)
    usedJ.add(j)
    res.push([i, j, Math.sqrt(d2)])
  }
  return res
}

/** Median nearest-neighbour distance within a single cloud (camera spacing). */
function medianSpacing(Q: Pt[]): number {
  if (Q.length < 2) return 0
  const nn: number[] = []
  for (let i = 0; i < Q.length; i++) {
    let best = Infinity
    for (let j = 0; j < Q.length; j++) {
      if (i === j) continue
      const dx = Q[i].x - Q[j].x
      const dy = Q[i].y - Q[j].y
      const d2 = dx * dx + dy * dy
      if (d2 < best) best = d2
    }
    if (best < Infinity) nn.push(Math.sqrt(best))
  }
  nn.sort((a, b) => a - b)
  return nn[Math.floor(nn.length / 2)] || 0
}

interface Transform {
  s: number
  fy: number
  tx: number
  ty: number
}

function applyT(p: Pt, t: Transform): Pt {
  return { x: t.s * p.x + t.tx, y: t.s * (t.fy * p.y) + t.ty }
}

/**
 * Estimate a similarity transform (uniform scale + translation + optional Y
 * flip, NO rotation) mapping P → Q, refined with a few ICP iterations.
 * Returns the transform plus the median residual after fitting.
 */
function estimateForFlip(P: Pt[], Q: Pt[], fy: number): { t: Transform; medResidual: number } {
  const Pf = P.map(p => ({ x: p.x, y: fy * p.y }))
  const cP = centroid(Pf)
  const cQ = centroid(Q)
  let s = rms(Q, cQ) / (rms(Pf, cP) || 1)
  let tx = cQ.x - s * cP.x
  let ty = cQ.y - s * cP.y

  for (let iter = 0; iter < 5; iter++) {
    const Pp = Pf.map(p => ({ x: s * p.x + tx, y: s * p.y + ty }))
    const pairs = nearestPairs(Pp, Q)
    if (pairs.length === 0) break
    const mp = pairs.map(([i, j]) => ({ p: Pf[i], q: Q[j] }))
    const cp = centroid(mp.map(m => m.p))
    const cq = centroid(mp.map(m => m.q))
    let num = 0
    let den = 0
    for (const m of mp) {
      num += (m.p.x - cp.x) * (m.q.x - cq.x) + (m.p.y - cp.y) * (m.q.y - cq.y)
      den += (m.p.x - cp.x) ** 2 + (m.p.y - cp.y) ** 2
    }
    if (den > 0) s = num / den
    tx = cq.x - s * cp.x
    ty = cq.y - s * cp.y
  }

  const t: Transform = { s, fy, tx, ty }
  const Pp = P.map(p => applyT(p, t))
  const pairs = nearestPairs(Pp, Q)
  const resid = pairs.map(p => p[2]).sort((a, b) => a - b)
  const medResidual = resid[Math.floor(resid.length / 2)] ?? Infinity
  return { t, medResidual }
}

/**
 * 1:1 match by CSV EntityHandle. Returns null when the sidecar has no handles or
 * too few of them hit (different conversion) so the caller falls back to
 * coordinate alignment.
 */
function matchByHandle(
  items: SidecarItem[],
  elements: CctvElementLike[],
  cctvCount: number
): CctvIpMatchResult | null {
  const withHandle = items.filter(i => i.handle)
  if (withHandle.length === 0) return null
  const byHandle = new Map<string, string>()
  for (const el of elements) {
    const data = el.get('data')
    if (!isCctvElement(data) || !data?.entityHandle) continue
    byHandle.set(data.entityHandle, String(el.id))
  }
  const updates: CctvIpUpdate[] = []
  const usedIds = new Set<string>()
  const matchedIps = new Set<string>()
  for (const it of withHandle) {
    const id = byHandle.get(it.handle as string)
    if (!id || usedIds.has(id)) continue
    usedIds.add(id)
    matchedIps.add(it.ip)
    updates.push({ id, ip: it.ip })
  }
  if (updates.length / withHandle.length < MIN_HANDLE_HIT_FRACTION) return null
  return {
    updates,
    matched: updates.length,
    totalItems: items.length,
    cctvCount,
    unmatchedIps: items.filter(i => !matchedIps.has(i.ip)).map(i => i.ip),
    aligned: true,
  }
}

/**
 * Match sidecar items to CCTV elements.
 *
 * Strategy:
 *  - If items carry CSV handles that resolve to placed elements, match 1:1 by
 *    handle (see `matchByHandle`) and skip alignment.
 *  - Collect CCTV elements (discriminator) and their representative points.
 *  - If both clouds are large enough, estimate the meter→paper similarity
 *    transform (trying both Y orientations) and match transformed items to
 *    cameras within a spacing-relative tolerance.
 *  - If the best alignment is too poor, return `aligned: false` and no updates.
 */
export function matchCctvIpsToElements(
  items: SidecarItem[],
  elements: CctvElementLike[]
): CctvIpMatchResult {
  const cams: Array<{ id: string; pt: Pt }> = []
  for (const el of elements) {
    const data = el.get('data')
    if (!isCctvElement(data)) continue
    const pt = elementPoint(data)
    if (!pt) continue
    cams.push({ id: String(el.id), pt })
  }

  const empty = (aligned: boolean): CctvIpMatchResult => ({
    updates: [],
    matched: 0,
    totalItems: items.length,
    cctvCount: cams.length,
    unmatchedIps: items.map(i => i.ip),
    aligned,
  })

  if (cams.length === 0 || items.length === 0) return empty(false)

  const byHandle = matchByHandle(items, elements, cams.length)
  if (byHandle) return byHandle

  const P = items.map(i => ({ x: i.x, y: i.y }))
  const Q = cams.map(c => c.pt)

  // Too few points to estimate a transform reliably: fall back to a direct
  // match (assumes the two clouds already share a coordinate space, e.g. unit
  // tests or fresh meter-space imports).
  let best: { t: Transform } | null = null
  if (P.length >= MIN_POINTS_FOR_TRANSFORM && Q.length >= MIN_POINTS_FOR_TRANSFORM) {
    const a = estimateForFlip(P, Q, 1)
    const b = estimateForFlip(P, Q, -1)
    best = a.medResidual <= b.medResidual ? { t: a.t } : { t: b.t }
  } else {
    best = { t: { s: 1, fy: 1, tx: 0, ty: 0 } }
  }

  const Pp = P.map(p => applyT(p, best.t))
  const spacing = medianSpacing(Q)
  // Tolerance: a fraction of camera spacing; when spacing is unknown (≤1 cam)
  // fall back to an absolute slack in the cloud's own units.
  const tol = spacing > 0 ? spacing * INLIER_SPACING_FRACTION : rms(Q, centroid(Q)) * 0.1 + 1

  const pairs = nearestPairs(Pp, Q)
  const usedItem = new Set<number>()
  const updates: CctvIpUpdate[] = []
  for (const [i, j, dist] of pairs) {
    if (dist > tol) continue
    usedItem.add(i)
    updates.push({ id: cams[j].id, ip: items[i].ip })
  }

  const inlierFraction = updates.length / Math.min(P.length, Q.length)
  const aligned = inlierFraction >= MIN_INLIER_FRACTION
  if (!aligned) return empty(false)

  const unmatchedIps = items.filter((_, i) => !usedItem.has(i)).map(i => i.ip)
  return {
    updates,
    matched: updates.length,
    totalItems: items.length,
    cctvCount: cams.length,
    unmatchedIps,
    aligned,
  }
}
